import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
import { useGridApiRef } from '@mui/x-data-grid';
import * as yup from 'yup';
import dayjs from 'dayjs';
import * as chanceApi from '../../lib/api/chance';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { SalesEstimateListBox } from '../../components/sales_estimate/SalesEstimateListBox';
import { Box, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import {
  listSalesEstimate,
  setSalesEstimateFilters,
  resetSalesEstimateFilters,
} from '../../modules/chanceModule/sales-estimate';
import SalesEstimateList from '../../components/sales_estimate/SalesEstimateList';
import EstimatePreviewModal from '../../components/sales_estimate/EstimatePreviewModal';
import EstimateEditModal from '../../components/sales_estimate/EstimateEditModal';

const SalesEstimateListContainer = () => {
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { enqueueSnackbar } = useSnackbar();

  const { files, defaultFilters, error, loading } = useSelector(
    ({ chanceModule, loading }) => ({
      files: chanceModule.salesEstimate.files,
      defaultFilters: chanceModule.salesEstimate.defaultFilters,
      error: chanceModule.salesEstimate.error,
      loading: loading['chance/LIST_SALES_ESTIMATE'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listSalesEstimate(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (files) {
      setTableData(files);
    }
  }, [files]);

  const handleFilters = useCallback((name, value) => {
    const newFilters = { ...defaultFilters, [name]: value };

    dispatch(setSalesEstimateFilters(newFilters));
  }, []);

  const [showPreview, setShowPreview] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);

  const handleClose = () => {
    setShowPreview(false);
    setSelectedEstimate(null);
  };

  const handlePreview = (param) => {
    // modal창 오픈
    setSelectedEstimate(param);
    setShowPreview(true);
  };

  const [showEdit, setShowEdit] = useState(false);
  const [selectedEdit, setSelectedEdit] = useState(null);
  const handleEdit = (param) => {
    setSelectedEdit(param);
    setShowEdit(true);
  };

  const handleEditClose = () => {
    setShowEdit(false);
    setSelectedEdit(null);
  };

  const handleSave = async (changeDate) => {
    try {
      const dateToSubmit = {
        prjUuid: selectedEdit.prjUuid,
        qteUuid: selectedEdit.qteUuid,
        qteRegDt: dayjs(changeDate).format('YYYY-MM-DD'),
      };
      const response = await chanceApi.editEstimate(dateToSubmit);
      if (response.data.info) {
        dispatch(listSalesEstimate(makeFilters(defaultFilters)));
        enqueueSnackbar('저장 되었습니다', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'qteUuid',
        headerName: '견적서ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'prjUuid',
        headerName: '프로젝트ID',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'qtePartnerUuid',
        hide: true,
        type: 'string',
        editable: false,
        disableExport: true,
      },
      {
        field: 'qteRegDtAction',
        headerName: '',
        minWidth: 20,
        flex: 0.1,
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        editable: false,
        renderCell: (params) => (
          <IconButton
            aria-label="edit"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon />
          </IconButton>
        ),
      },
      {
        field: 'qteRegDt',
        headerName: '견적일자',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        editable: false,
      },
      {
        field: 'qtePartnerNm',
        headerName: '매출처명',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },
      // {
      //   field: 'vendorCmpNm',
      //   headerName: '매출처명',
      //   minWidth: 200,
      //   flex: 1,
      //   type: 'string',
      //   align: 'left',
      //   headerAlign: 'left',
      //   editable: false,
      // },

      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 400,
        flex: 1,
        description: '',
        align: 'left',
        headerAlign: 'left',
        editable: false,
        renderCell: (params) => (
          <>
            {params.value}
            <IconButton
              aria-label="preview"
              color="success"
              size="small"
              onClick={() => handlePreview(params.row)}
            >
              <PreviewIcon />
            </IconButton>
          </>
        ),
      },

      {
        field: 'qteCrtNm',
        headerName: '견적등록자',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        editable: false,
      },

      {
        field: 'qteCrtDt',
        headerName: '견적등록일',
        minWidth: 100,
        flex: 0.5,
        type: 'string',
        align: 'center',
        headerAlign: 'center',
        editable: false,
      },
    ],
    [],
  );

  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword,
    searchWords: defaultFilters.searchWords,
    searchStrtDt: defaultFilters.searchStrtDt,
    searchEndDt: defaultFilters.searchEndDt,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '검색 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        let searchData = {
          searchKeyword: values.searchKeyword,
          searchWords: values.searchWords,
          searchStrtDt: values.searchStrtDt,
          searchEndDt: values.searchEndDt,
        };
        dispatch(setSalesEstimateFilters(searchData));

        setSubmitting(false);
      }, 1000);
    },
  });

  return (
    <>
      <BreadcrumbsContainer
        title="포캐스팅 관리/매출 견적서 현황/매출 견적서 리스트"
        icon="chance"
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <SalesEstimateListBox formik={formik} onReset={() => dispatch(resetSalesEstimateFilters())} />
        <SalesEstimateList
          apiRef={apiRef}
          columns={columns}
          rows={tableData}
          error={error}
          loading={loading}
          columnVisibilityModel={{
            // Hide columns status and traderName, the other columns will remain visible
            id: false,
            qteUuid: false,
            prjUuid: false,
            qtePartnerUuid: false,
          }}
        />
      </Box>
      {showPreview && (
        <EstimatePreviewModal
          estimate={selectedEstimate}
          open={showPreview}
          handleClose={handleClose}
        />
      )}
      {showEdit && (
        <EstimateEditModal
          currentData={selectedEdit}
          open={showEdit}
          handleClose={handleEditClose}
          onSave={handleSave}
        />
      )}
    </>
  );
};

export default SalesEstimateListContainer;
